import { ChevronsUpDown, Copy, Eye, EyeOff, Trash2 } from 'lucide-react'
import { Fragment, useMemo, useState, type ReactNode } from 'react'
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { useOnboarding } from '../../context/OnboardingContext'
import type { LicenseRow, StateLoginRow } from '../../types/onboarding'
import { companyNameOptions } from '../licenseSteps/businessEntityNames'
import { licenseeNameOptionsFromLicenses } from '../licenseSteps/licenseeNameOptions'
import { loginRowCompleteForPrimaryStep } from '../licenseSteps/loginRowComplete'
import { isCustomStateLoginRowId, isIllinoisLoginRow, illinoisIndividualsForLogins, loginStateColumnLabel } from '../../data/stateLoginRegistry'
import {
  PRIMARY_STATE_LOGINS_COLUMN_WIDTHS,
  PRIMARY_STATE_LOGINS_COLUMN_WIDTHS_WITH_REMOVE,
  WIZARD_STATE_LOGINS_COLUMN_WIDTHS,
} from '../constants/stateLoginsLayout'
import { MISC_PRIMARY, MISC_PRIMARY_HOVER } from '../../theme/onboardingTheme'

export type StateLoginsTableMode = 'primary' | 'wizard'

/** State column: 2-letter code for jurisdictions; OPTins / SLIP+ labels unchanged from workbook. */
function stateCellLabel(row: StateLoginRow): string {
  if (row.licenseJurisdictionCode && row.licenseJurisdictionCode.length === 2) {
    return row.licenseJurisdictionCode.toUpperCase()
  }
  return loginStateColumnLabel(row.stateCode)
}

function portalHost(url: string): string {
  try {
    return new URL(url).hostname
  } catch {
    return url
  }
}

/** Fixed row height so disabled / deferred rows align with rows that show copy & eye icons. */
const LOGIN_CELL_H_PX = 36

/**
 * Shared sizing for inputs, selects, and faux-input spans (explicit height beats UA disabled padding).
 */
const loginFieldSx = {
  boxSizing: 'border-box' as const,
  minHeight: 0,
  height: LOGIN_CELL_H_PX,
  borderRadius: '4px',
  border: '1px solid',
  px: 1,
  py: 0,
  textAlign: 'center' as const,
  fontSize: 12,
  lineHeight: 1.375,
}

/** Icon column width matches `rounded p-1` + 14px icon (~1.75rem). */
const loginIconSlotSx = {
  display: 'inline-flex',
  height: LOGIN_CELL_H_PX,
  width: 28,
  flexShrink: 0,
  alignItems: 'center',
  justifyContent: 'center',
}

const compactFieldSx = {
  flex: 1,
  minWidth: 0,
  '& .MuiOutlinedInput-root': {
    height: LOGIN_CELL_H_PX,
    fontSize: 12,
    borderRadius: '4px',
    backgroundColor: '#ffffff',
  },
  '& .MuiOutlinedInput-input': {
    textAlign: 'center',
    py: 0,
    px: 1,
    height: LOGIN_CELL_H_PX,
    boxSizing: 'border-box',
  },
  '& .Mui-disabled': {
    cursor: 'not-allowed',
    backgroundColor: '#f1f5f9',
    color: '#94a3b8',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
  },
  '& .MuiInputBase-readOnly': {
    backgroundColor: '#ffffff',
    color: '#0f172a',
  },
}

const compactSelectSx = {
  width: '100%',
  minWidth: 0,
  maxHeight: LOGIN_CELL_H_PX,
  height: LOGIN_CELL_H_PX,
  fontSize: 12,
  fontWeight: 500,
  color: '#0f172a',
  backgroundColor: '#ffffff',
  textAlign: 'center',
  '& .MuiSelect-select': {
    py: 0,
    px: 1,
    height: LOGIN_CELL_H_PX,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
  },
  '&.Mui-disabled': {
    cursor: 'not-allowed',
    backgroundColor: '#f1f5f9',
    color: '#94a3b8',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
  },
}

const iconBtnSx = {
  borderRadius: '4px',
  p: 0.5,
  color: '#94a3b8',
  '&:hover': { backgroundColor: '#e2e8f0', color: '#334155' },
  '&.Mui-disabled': { opacity: 0.4 },
}

function SortableTh({
  children,
  sx,
}: {
  children: ReactNode
  sx?: object
}) {
  return (
    <TableCell
      component="th"
      sx={{
        px: { xs: 1, lg: 1.5 },
        py: 1.25,
        textAlign: 'center',
        fontSize: 14,
        fontWeight: 600,
        color: '#0f172a',
        textTransform: 'none',
        ...sx,
      }}
    >
      <Box
        component="span"
        sx={{ display: 'inline-flex', minWidth: 0, alignItems: 'center', justifyContent: 'center', gap: 0.5 }}
      >
        <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {children}
        </Box>
        <ChevronsUpDown size={14} color="#94a3b8" aria-hidden />
      </Box>
    </TableCell>
  )
}

export function StateLoginsTable({
  rows,
  mode,
  readOnly = false,
  /** When true with `readOnly`, show the same link/copy/eye affordances as SL Logins (no edits). */
  credentialReference = false,
}: {
  rows: StateLoginRow[]
  mode: StateLoginsTableMode
  /** When true, all credential cells are read-only (e.g. OPTins setup guide). */
  readOnly?: boolean
  credentialReference?: boolean
}) {
  const { state, dispatch } = useOnboarding()
  const [pwVisible, setPwVisible] = useState<Record<string, boolean>>({})
  const [openLicenseeRow, setOpenLicenseeRow] = useState<string | null>(null)
  const [illinoisPanelOpen, setIllinoisPanelOpen] = useState(false)

  const illinoisChooserActive =
    mode === 'primary' &&
    !readOnly &&
    state.activeBusinessStates.some((code) => code.toUpperCase() === 'IL')

  const illinoisIndividualCount = useMemo(
    () => illinoisIndividualsForLogins(state.licenses).length,
    [state.licenses],
  )

  // Keep the panel open when individual mode has no matching licenses yet.
  const illinoisNeedsEmptyGuidance =
    illinoisChooserActive &&
    state.illinoisLoginMode === 'individual' &&
    illinoisIndividualCount === 0

  const illinoisPanelVisible = illinoisPanelOpen || illinoisNeedsEmptyGuidance

  const lastIllinoisRowIndex = useMemo(() => {
    let last = -1
    rows.forEach((row, index) => {
      if (isIllinoisLoginRow(row)) last = index
    })
    return last
  }, [rows])

  const entityNameOptions = useMemo(
    () => companyNameOptions(state.licenses, rows.map((r) => r.clientCompanyName)),
    [state.licenses, rows],
  )

  const licenseeNameOptions = useMemo(
    () => licenseeNameOptionsFromLicenses(state.licenses, rows.map((r) => r.licenseeDisplay)),
    [state.licenses, rows],
  )

  const copyText = async (label: string, text: string) => {
    if (!text.trim()) {
      dispatch({ type: 'TOAST', message: 'Nothing to copy.' })
      return
    }
    try {
      await navigator.clipboard.writeText(text)
      dispatch({ type: 'TOAST', message: `${label} copied to clipboard.` })
    } catch {
      dispatch({ type: 'TOAST', message: 'Copy not available in this browser.' })
    }
  }

  const showDeferColumn = mode === 'primary'
  const showRemoveColumn =
    mode === 'primary' &&
    !readOnly &&
    rows.some((r) => isCustomStateLoginRowId(r.stateCode))
  const colWidths = showDeferColumn
    ? showRemoveColumn
      ? PRIMARY_STATE_LOGINS_COLUMN_WIDTHS_WITH_REMOVE
      : PRIMARY_STATE_LOGINS_COLUMN_WIDTHS
    : WIZARD_STATE_LOGINS_COLUMN_WIDTHS

  return (
    <Paper
      elevation={0}
      sx={{
        minWidth: 0,
        overflow: 'visible',
        borderRadius: '8px',
        border: '1px solid #e2e8f0',
        backgroundColor: '#ffffff',
        boxShadow: 1,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Table
          sx={{
            width: '100%',
            tableLayout: 'fixed',
            borderCollapse: 'collapse',
            textAlign: 'center',
            fontSize: 14,
          }}
        >
          <colgroup>
            {colWidths.map((w, i) => (
              <col key={`${mode}-${i}`} style={{ width: w }} />
            ))}
          </colgroup>
          <TableHead>
            <TableRow sx={{ borderBottom: '1px solid #e2e8f0', backgroundColor: '#f1f5f9' }}>
              <SortableTh>Licensee</SortableTh>
              <SortableTh>State</SortableTh>
              <SortableTh>Website</SortableTh>
              <SortableTh>Username</SortableTh>
              <SortableTh>Password</SortableTh>
              <SortableTh>Business Entity</SortableTh>
              {showDeferColumn ? (
                <SortableTh>No Login</SortableTh>
              ) : null}
              {showRemoveColumn ? (
                <TableCell
                  component="th"
                  sx={{
                    px: { xs: 1, lg: 1.5 },
                    py: 1.25,
                    width: '8%',
                    border: 0,
                    textAlign: 'center',
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#0f172a',
                    textTransform: 'none',
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      minWidth: 0,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    Action
                  </Box>
                </TableCell>
              ) : null}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => {
              const zebra = index % 2 === 0 ? '#ffffff' : 'rgba(248, 250, 252, 0.7)'
              const showPw = pwVisible[row.stateCode] ?? false
              const deferredPrimary = mode === 'primary' && row.pendingLoginWizard
              const illinoisPending = Boolean(row.illinoisPendingChoice)
              /** Muted “locked” styling (gray website, no copy). */
              const grayLocked =
                row.miscManaged ||
                deferredPrimary ||
                illinoisPending ||
                (readOnly && !credentialReference)
              /** Inputs cannot change; with `credentialReference`, fields still look like SL Logins. */
              const showCopyAffordances =
                !row.miscManaged &&
                !deferredPrimary &&
                !illinoisPending &&
                (!readOnly || credentialReference)
              const rowMuted = deferredPrimary || illinoisPending ? '#f8fafc' : ''
              const rowLicenseeOptions = Array.from(
                new Set(
                  [...licenseeNameOptions, row.licenseeDisplay]
                    .map((name) => name.trim())
                    .filter(Boolean),
                ),
              )
              const illinoisRow = illinoisChooserActive && isIllinoisLoginRow(row)
              const showIllinoisPanel =
                illinoisChooserActive && illinoisPanelVisible && index === lastIllinoisRowIndex

              return (
                <Fragment key={row.stateCode}>
                <TableRow
                  sx={{
                    borderBottom: '1px solid #e2e8f0',
                    verticalAlign: 'middle',
                    backgroundColor: rowMuted || zebra,
                  }}
                >
                  <TableCell sx={{ minWidth: 0, overflow: 'visible', px: { xs: 1, lg: 1.5 }, py: 1.25 }}>
                    {row.miscManaged ? (
                      <Box
                        component="span"
                        title={row.licenseeDisplay?.trim() || undefined}
                        sx={{
                          ...loginFieldSx,
                          display: 'flex',
                          width: '100%',
                          minWidth: 0,
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontWeight: 500,
                          color: '#0f172a',
                          borderColor: '#e2e8f0',
                          backgroundColor: '#f8fafc',
                        }}
                      >
                        {row.licenseeDisplay?.trim() || ''}
                      </Box>
                    ) : (
                      <Box
                        sx={{ position: 'relative' }}
                        onBlur={(e) => {
                          const nextTarget = e.relatedTarget as Node | null
                          if (!nextTarget || !e.currentTarget.contains(nextTarget)) {
                            setOpenLicenseeRow(null)
                          }
                        }}
                      >
                        <Button
                          type="button"
                          disabled={grayLocked || (readOnly && credentialReference)}
                          aria-label={`Licensee for ${stateCellLabel(row)}`}
                          aria-haspopup="listbox"
                          aria-expanded={openLicenseeRow === row.stateCode}
                          onClick={() =>
                            setOpenLicenseeRow((current) =>
                              current === row.stateCode ? null : row.stateCode,
                            )
                          }
                          sx={{
                            ...loginFieldSx,
                            display: 'flex',
                            width: '100%',
                            minWidth: 0,
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 0.5,
                            fontWeight: 500,
                            color: '#0f172a',
                            borderColor: '#cbd5e1',
                            backgroundColor: '#ffffff',
                            textTransform: 'none',
                            '&.Mui-disabled': {
                              cursor: 'not-allowed',
                              borderColor: '#e2e8f0',
                              backgroundColor: '#f1f5f9',
                              color: '#94a3b8',
                            },
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              minWidth: 0,
                              flex: 1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              textAlign: 'center',
                            }}
                          >
                            {row.licenseeDisplay || ''}
                          </Box>
                          <ChevronsUpDown size={12} color="#94a3b8" aria-hidden />
                        </Button>
                        {openLicenseeRow === row.stateCode ? (
                          <Box
                            role="listbox"
                            sx={{
                              position: 'absolute',
                              left: 0,
                              top: '100%',
                              zIndex: 50,
                              mt: 0.5,
                              maxHeight: 224,
                              minWidth: '100%',
                              maxWidth: 'min(24rem, calc(100vw - 2rem))',
                              overflowY: 'auto',
                              borderRadius: '8px',
                              border: '1px solid #e2e8f0',
                              backgroundColor: '#ffffff',
                              py: 0.5,
                              textAlign: 'left',
                              boxShadow: 8,
                            }}
                          >
                            {rowLicenseeOptions.length > 0 ? (
                              rowLicenseeOptions.map((name) => (
                                <Button
                                  key={name}
                                  type="button"
                                  role="option"
                                  aria-selected={name === row.licenseeDisplay}
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => {
                                    dispatch({
                                      type: 'UPDATE_LOGIN',
                                      stateCode: row.stateCode,
                                      patch: { licenseeDisplay: name },
                                    })
                                    setOpenLicenseeRow(null)
                                  }}
                                  sx={{
                                    display: 'block',
                                    width: '100%',
                                    justifyContent: 'flex-start',
                                    px: 1.5,
                                    py: 1,
                                    textAlign: 'left',
                                    fontSize: 12,
                                    fontWeight: 500,
                                    color: '#0f172a',
                                    textTransform: 'none',
                                    borderRadius: 0,
                                    '&:hover': { backgroundColor: '#eff6ff', color: MISC_PRIMARY },
                                  }}
                                >
                                  {name}
                                </Button>
                              ))
                            ) : (
                              <Box sx={{ px: 1.5, py: 1, fontSize: 12, color: '#64748b' }}>
                                Add licensees first
                              </Box>
                            )}
                          </Box>
                        ) : null}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell
                    sx={{
                      minWidth: 0,
                      px: { xs: 1, lg: 1.5 },
                      py: 1.25,
                      fontWeight: 600,
                      color: '#0f172a',
                    }}
                  >
                    {illinoisRow ? (
                      <Button
                        type="button"
                        title={loginStateColumnLabel(row.stateCode)}
                        aria-expanded={illinoisPanelVisible}
                        onClick={() => setIllinoisPanelOpen((open) => !open)}
                        sx={{
                          display: 'inline-flex',
                          minHeight: LOGIN_CELL_H_PX,
                          width: '100%',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '4px',
                          border: '1px solid #cbd5e1',
                          backgroundColor: '#ffffff',
                          px: 1,
                          fontSize: 14,
                          fontWeight: 600,
                          color: MISC_PRIMARY,
                          textTransform: 'none',
                          '&:hover': { backgroundColor: '#f8fafc' },
                        }}
                      >
                        {stateCellLabel(row)}
                      </Button>
                    ) : (
                      <Box component="span" title={loginStateColumnLabel(row.stateCode)}>
                        {stateCellLabel(row)}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell sx={{ minWidth: 0, px: { xs: 1, lg: 1.5 }, py: 1.25 }}>
                    {row.miscManaged ? (
                      <Typography sx={{ display: 'block', textAlign: 'center', fontSize: 12, color: '#64748b' }}>
                        MISC-managed account
                      </Typography>
                    ) : grayLocked ? (
                      <Box
                        sx={{
                          display: 'flex',
                          minWidth: 0,
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 0.5,
                          height: LOGIN_CELL_H_PX,
                        }}
                      >
                        <Box
                          component="span"
                          title={row.portalUrl}
                          sx={{
                            ...loginFieldSx,
                            display: 'flex',
                            minWidth: 0,
                            flex: 1,
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            borderColor: '#e2e8f0',
                            backgroundColor: '#f1f5f9',
                            color: '#94a3b8',
                          }}
                        >
                          {portalHost(row.portalUrl)}
                        </Box>
                        <Box component="span" sx={loginIconSlotSx} aria-hidden />
                      </Box>
                    ) : isCustomStateLoginRowId(row.stateCode) ? (
                      <Box
                        sx={{
                          display: 'flex',
                          minWidth: 0,
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 0.5,
                          height: LOGIN_CELL_H_PX,
                        }}
                      >
                        <TextField
                          type="url"
                          hiddenLabel
                          size="small"
                          InputProps={{ readOnly: readOnly && credentialReference }}
                          disabled={grayLocked}
                          placeholder="https://…"
                          inputProps={{ 'aria-label': `Website URL for ${loginStateColumnLabel(row.stateCode)}` }}
                          value={row.portalUrl}
                          onChange={(e) =>
                            dispatch({
                              type: 'UPDATE_LOGIN',
                              stateCode: row.stateCode,
                              patch: { portalUrl: e.target.value },
                            })
                          }
                          sx={{
                            ...compactFieldSx,
                            '& .MuiOutlinedInput-root': {
                              ...compactFieldSx['& .MuiOutlinedInput-root'],
                              color: MISC_PRIMARY,
                            },
                          }}
                        />
                        <Box component="span" sx={loginIconSlotSx}>
                          <IconButton
                            type="button"
                            title="Copy website"
                            disabled={!row.portalUrl.trim()}
                            onClick={() => copyText('Website', row.portalUrl)}
                            sx={iconBtnSx}
                          >
                            <Copy size={14} />
                          </IconButton>
                        </Box>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          display: 'flex',
                          minWidth: 0,
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 0.5,
                          height: LOGIN_CELL_H_PX,
                        }}
                      >
                        <Link
                          href={row.portalUrl}
                          target="_blank"
                          rel="noreferrer"
                          title={row.portalUrl}
                          sx={{
                            display: 'flex',
                            minHeight: 0,
                            minWidth: 0,
                            flex: 1,
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            px: 0.5,
                            textAlign: 'center',
                            fontSize: 12,
                            color: MISC_PRIMARY,
                            '&:hover': { textDecoration: 'underline' },
                          }}
                        >
                          {portalHost(row.portalUrl)}
                        </Link>
                        <Box component="span" sx={loginIconSlotSx}>
                          <IconButton
                            type="button"
                            title="Copy website"
                            onClick={() => copyText('Website', row.portalUrl)}
                            sx={iconBtnSx}
                          >
                            <Copy size={14} />
                          </IconButton>
                        </Box>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell sx={{ minWidth: 0, px: { xs: 1, lg: 1.5 }, py: 1.25 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        minWidth: 0,
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.5,
                        height: LOGIN_CELL_H_PX,
                      }}
                    >
                      <TextField
                        hiddenLabel
                        size="small"
                        InputProps={{ readOnly: readOnly && credentialReference }}
                        disabled={grayLocked}
                        placeholder="Username"
                        value={row.username}
                        onChange={(e) =>
                          dispatch({
                            type: 'UPDATE_LOGIN',
                            stateCode: row.stateCode,
                            patch: { username: e.target.value },
                          })
                        }
                        sx={{
                          ...compactFieldSx,
                          '& .MuiOutlinedInput-root': {
                            ...compactFieldSx['& .MuiOutlinedInput-root'],
                            color: '#0f172a',
                          },
                        }}
                      />
                      <Box component="span" sx={loginIconSlotSx}>
                        {showCopyAffordances ? (
                          <IconButton
                            type="button"
                            title="Copy username"
                            onClick={() => copyText('Username', row.username)}
                            sx={iconBtnSx}
                          >
                            <Copy size={14} />
                          </IconButton>
                        ) : null}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ minWidth: 0, px: { xs: 1, lg: 1.5 }, py: 1.25 }}>
                    {row.miscManaged ? (
                      <Box
                        component="span"
                        sx={{
                          display: 'flex',
                          height: LOGIN_CELL_H_PX,
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 12,
                          color: '#94a3b8',
                        }}
                      >
                        —
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          display: 'flex',
                          minWidth: 0,
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 0.25,
                          height: LOGIN_CELL_H_PX,
                        }}
                      >
                        <TextField
                          type={showPw ? 'text' : 'password'}
                          hiddenLabel
                          size="small"
                          InputProps={{ readOnly: readOnly && credentialReference }}
                          disabled={grayLocked}
                          placeholder="••••••••"
                          value={row.password}
                          onChange={(e) =>
                            dispatch({
                              type: 'UPDATE_LOGIN',
                              stateCode: row.stateCode,
                              patch: { password: e.target.value },
                            })
                          }
                          sx={{
                            ...compactFieldSx,
                            '& .MuiOutlinedInput-root': {
                              ...compactFieldSx['& .MuiOutlinedInput-root'],
                              color: '#0f172a',
                            },
                          }}
                        />
                        <Box component="span" sx={loginIconSlotSx}>
                          {showCopyAffordances ? (
                            <IconButton
                              type="button"
                              title={showPw ? 'Hide password' : 'Show password'}
                              onClick={() =>
                                setPwVisible((m) => ({ ...m, [row.stateCode]: !showPw }))
                              }
                              sx={iconBtnSx}
                            >
                              {showPw ? (
                                <EyeOff size={14} />
                              ) : (
                                <Eye size={14} />
                              )}
                            </IconButton>
                          ) : null}
                        </Box>
                        <Box component="span" sx={loginIconSlotSx}>
                          {showCopyAffordances ? (
                            <IconButton
                              type="button"
                              title="Copy password"
                              onClick={() => copyText('Password', row.password)}
                              sx={iconBtnSx}
                            >
                              <Copy size={14} />
                            </IconButton>
                          ) : null}
                        </Box>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell sx={{ minWidth: 0, px: { xs: 1, lg: 1.5 }, py: 1.25, fontSize: 12 }}>
                    {row.miscManaged ? (
                      <Box
                        component="span"
                        title={
                          row.clientCompanyName.trim() ||
                          (entityNameOptions.length === 1 ? entityNameOptions[0] : undefined)
                        }
                        sx={{
                          ...loginFieldSx,
                          display: 'flex',
                          width: '100%',
                          minWidth: 0,
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontWeight: 500,
                          color: '#0f172a',
                          borderColor: '#e2e8f0',
                          backgroundColor: '#f8fafc',
                        }}
                      >
                        {row.clientCompanyName.trim() ||
                          (entityNameOptions.length === 1 ? entityNameOptions[0] : '') ||
                          '—'}
                      </Box>
                    ) : entityNameOptions.length === 0 ? (
                      <Select
                        disabled
                        displayEmpty
                        size="small"
                        aria-label="Client"
                        value=""
                        sx={{
                          ...compactSelectSx,
                          color: '#94a3b8',
                          backgroundColor: '#f1f5f9',
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
                        }}
                      >
                        <MenuItem value="">Add entities on Business Licenses first</MenuItem>
                      </Select>
                    ) : entityNameOptions.length === 1 ? (
                      <Box
                        component="span"
                        title={entityNameOptions[0]}
                        sx={{
                          ...loginFieldSx,
                          display: 'flex',
                          width: '100%',
                          minWidth: 0,
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontWeight: 500,
                          borderColor: '#e2e8f0',
                          ...(deferredPrimary
                            ? { color: '#94a3b8', backgroundColor: '#f1f5f9' }
                            : { color: '#0f172a', backgroundColor: '#f8fafc' }),
                        }}
                      >
                        {entityNameOptions[0]}
                      </Box>
                    ) : readOnly && credentialReference ? (
                      <Box
                        component="span"
                        title={row.clientCompanyName.trim() || undefined}
                        sx={{
                          ...loginFieldSx,
                          display: 'flex',
                          width: '100%',
                          minWidth: 0,
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontWeight: 500,
                          color: '#0f172a',
                          borderColor: '#e2e8f0',
                          backgroundColor: '#f8fafc',
                        }}
                      >
                        {row.clientCompanyName.trim() || '—'}
                      </Box>
                    ) : (
                      <Select
                        disabled={grayLocked}
                        displayEmpty
                        size="small"
                        value={row.clientCompanyName}
                        onChange={(e) =>
                          dispatch({
                            type: 'UPDATE_LOGIN',
                            stateCode: row.stateCode,
                            patch: { clientCompanyName: e.target.value as string },
                          })
                        }
                        aria-label={`Client for ${stateCellLabel(row)}`}
                        sx={compactSelectSx}
                      >
                        <MenuItem value="">Select entity…</MenuItem>
                        {entityNameOptions.map((name) => (
                          <MenuItem key={name} value={name}>
                            {name}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  </TableCell>
                  {showDeferColumn ? (
                    <TableCell sx={{ minWidth: 0, px: { xs: 1, lg: 1.5 }, py: 1.25, verticalAlign: 'middle' }}>
                      {row.miscManaged || illinoisPending ? null : (
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <Box
                            component="label"
                            sx={{
                              display: 'inline-flex',
                              cursor: 'pointer',
                              alignItems: 'center',
                              gap: 1,
                              fontSize: 12,
                              color: '#334155',
                            }}
                          >
                            <Checkbox
                              size="small"
                              inputProps={{
                                'aria-label': 'No login yet; check here to defer setup to the New Login Wizard',
                              }}
                              checked={row.pendingLoginWizard}
                              onChange={(e) =>
                                dispatch({
                                  type: 'UPDATE_LOGIN',
                                  stateCode: row.stateCode,
                                  patch: { pendingLoginWizard: e.target.checked },
                                })
                              }
                              sx={{ p: 0, color: '#cbd5e1' }}
                            />
                          </Box>
                        </Box>
                      )}
                    </TableCell>
                  ) : null}
                  {showRemoveColumn ? (
                    <TableCell sx={{ minWidth: 0, px: { xs: 0.5, lg: 1 }, py: 1.25, verticalAlign: 'middle' }}>
                      {isCustomStateLoginRowId(row.stateCode) ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <IconButton
                            type="button"
                            aria-label="Remove custom state login"
                            title="Remove"
                            onClick={() =>
                              dispatch({ type: 'REMOVE_STATE_LOGIN', stateCode: row.stateCode })
                            }
                            sx={{
                              color: '#64748b',
                              '&:hover': { color: '#b91c1c', backgroundColor: '#fef2f2' },
                            }}
                          >
                            <Trash2 size={16} />
                          </IconButton>
                        </Box>
                      ) : null}
                    </TableCell>
                  ) : null}
                </TableRow>
                {showIllinoisPanel ? (
                  <TableRow sx={{ borderBottom: '1px solid #e2e8f0', backgroundColor: 'rgba(240, 249, 255, 0.8)' }}>
                    <TableCell colSpan={colWidths.length} sx={{ px: 2, py: 2, textAlign: 'left' }}>
                      <Box sx={{ mx: 'auto', maxWidth: '48rem' }}>
                        {state.illinoisLoginMode === 'unset' || illinoisNeedsEmptyGuidance ? (
                          <>
                            <Typography sx={{ fontSize: 14, lineHeight: 1.625, color: '#1e293b' }}>
                              Zero reports are required through this system. Please provide a login
                              for each <Box component="strong" sx={{ fontWeight: 700 }}>INDIVIDUAL</Box> who holds a
                              surplus lines license in this state or a login to your Corporate
                              Account if you have set up a multi-licensee membership
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                              <Button
                                type="button"
                                onClick={() => {
                                  dispatch({ type: 'SET_ILLINOIS_LOGIN_MODE', mode: 'individual' })
                                  setIllinoisPanelOpen(false)
                                }}
                                sx={{
                                  borderRadius: '4px',
                                  backgroundColor: MISC_PRIMARY,
                                  px: 1.5,
                                  py: 1,
                                  fontSize: 12,
                                  fontWeight: 600,
                                  color: '#ffffff',
                                  boxShadow: 1,
                                  textTransform: 'none',
                                  '&:hover': { backgroundColor: MISC_PRIMARY_HOVER },
                                }}
                              >
                                I have the individual logins
                              </Button>
                              <Button
                                type="button"
                                onClick={() => {
                                  dispatch({ type: 'SET_ILLINOIS_LOGIN_MODE', mode: 'corporate' })
                                  setIllinoisPanelOpen(false)
                                }}
                                sx={{
                                  borderRadius: '4px',
                                  border: '1px solid #cbd5e1',
                                  backgroundColor: '#ffffff',
                                  px: 1.5,
                                  py: 1,
                                  fontSize: 12,
                                  fontWeight: 600,
                                  color: '#1e293b',
                                  boxShadow: 1,
                                  textTransform: 'none',
                                  '&:hover': { backgroundColor: '#f8fafc' },
                                }}
                              >
                                I have a Corporate Account
                              </Button>
                            </Box>
                            {illinoisIndividualCount === 0 ? (
                              <Typography sx={{ mt: 1.5, fontSize: 12, color: '#92400e' }}>
                                No Individual Licenses with Resident SL License State = IL yet. Add
                                them on the Individual Licenses step, then choose individual logins.
                              </Typography>
                            ) : null}
                          </>
                        ) : (
                          <Box
                            sx={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 1.5,
                            }}
                          >
                            <Typography sx={{ fontSize: 14, color: '#1e293b' }}>
                              Illinois login type:{' '}
                              <Box component="strong" sx={{ fontWeight: 600 }}>
                                {state.illinoisLoginMode === 'individual'
                                  ? 'Individual logins'
                                  : 'Corporate Account'}
                              </Box>
                              {state.illinoisLoginMode === 'individual'
                                ? ` (${illinoisIndividualCount} row${illinoisIndividualCount === 1 ? '' : 's'})`
                                : null}
                            </Typography>
                            <Button
                              type="button"
                              onClick={() => {
                                dispatch({ type: 'SET_ILLINOIS_LOGIN_MODE', mode: 'unset' })
                                setIllinoisPanelOpen(true)
                              }}
                              sx={{
                                borderRadius: '4px',
                                border: '1px solid #cbd5e1',
                                backgroundColor: '#ffffff',
                                px: 1.5,
                                py: 0.75,
                                fontSize: 12,
                                fontWeight: 600,
                                color: '#1e293b',
                                textTransform: 'none',
                                '&:hover': { backgroundColor: '#f8fafc' },
                              }}
                            >
                              Change
                            </Button>
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : null}
                </Fragment>
              )
            })}
          </TableBody>
        </Table>
      </Box>
      <Box
        sx={{
          borderTop: '1px solid #e2e8f0',
          backgroundColor: '#f8fafc',
          px: 2,
          py: 1,
          textAlign: 'center',
          fontSize: 12,
          color: '#475569',
        }}
      >
        Total Rows: {rows.length}
      </Box>
    </Paper>
  )
}

export function countPrimaryLoginProgress(rows: StateLoginRow[], licenses: LicenseRow[]) {
  const needed = rows.filter((s) => !s.miscManaged)
  const done = needed.filter((s) => loginRowCompleteForPrimaryStep(s, licenses)).length
  return { needed, done }
}
